import Components from './../Component';

const MIN_LENGTH = 3;
const DELAY = 250;

/**
 * Suggestions under the keyword field of the news and agenda listings.
 *
 * The suggestions are the contents themselves: choosing one opens its page.
 * ARIA combobox: the focus stays in the field, the arrows move through the
 * list (aria-activedescendant), Enter opens the selected content or, with none
 * selected, submits the form as before, Escape closes the list.
 *
 * With data-suggest-all (the articles of Ons Stad), the list ends with that
 * label as one more option: it closes the list and submits the form, the
 * search applied to the whole page.
 */
export default class KeywordSuggest extends Components {

  constructor($) {
    super($);
  }

  init(index, input) {
    const listbox = document.createElement('ul');
    listbox.id = input.id + '-suggestions';
    listbox.hidden = true;
    listbox.setAttribute('role', 'listbox');
    listbox.setAttribute('aria-label', input.dataset.suggestLabel);
    listbox.className = 'tw:absolute tw:left-0 tw:top-full tw:z-20 tw:mt-2 tw:py-2 tw:bg-white tw:rounded-3xl tw:shadow-lg';
    input.parentNode.classList.add('tw:relative');
    input.parentNode.appendChild(listbox);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', listbox.id);

    const state = { input, listbox, items: [], active: -1, timer: null, request: null };

    input.addEventListener('input', () => {
      // The list shown answers the previous keywords: until the new one comes,
      // Enter submits what is typed rather than a suggestion picked before.
      this.deselect(state);
      clearTimeout(state.timer);
      state.timer = setTimeout(() => this.lookup(state), DELAY);
    });
    input.addEventListener('keydown', (e) => this.keydown(state, e));
    input.addEventListener('blur', () => this.close(state));
    // Keep the focus in the field while an option is clicked.
    listbox.addEventListener('mousedown', (e) => e.preventDefault());
  }

  lookup(state) {
    const keywords = state.input.value.trim();
    if (state.request) {
      state.request.abort();
    }
    if (keywords.length < MIN_LENGTH) {
      this.render(state, []);
      return;
    }

    state.request = new AbortController();
    fetch(state.input.dataset.suggestUrl + '?q=' + encodeURIComponent(keywords), {
      headers: { Accept: 'application/json' },
      signal: state.request.signal
    })
      .then((response) => response.json())
      .then((data) => {
        // An answer to keywords typed over since is dropped.
        if (state.input.value.trim() === keywords) {
          this.render(state, data.items || []);
        }
      })
      .catch(() => {
        // Aborted or failed: the field still submits the form.
      });
  }

  render(state, items) {
    state.items = items;
    state.active = -1;
    state.listbox.replaceChildren();
    state.input.removeAttribute('aria-activedescendant');

    items.forEach((item, i) => {
      const option = document.createElement('li');
      option.id = state.listbox.id + '-' + i;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.className = 'tw:px-6 tw:py-2 tw:cursor-pointer tw:aria-selected:bg-green-100 tw:hover:bg-green-50';

      const title = document.createElement('span');
      // app.css, unlayered, outranks the utilities on the text of list items.
      title.className = 'tw:block tw:font-semibold! tw:text-blue-500!';
      title.textContent = item.title;
      option.appendChild(title);

      if (item.meta) {
        const meta = document.createElement('span');
        meta.className = 'tw:block tw:text-sm! tw:text-blue-400!';
        meta.textContent = item.meta;
        option.appendChild(meta);
      }

      option.addEventListener('click', () => this.open(item));
      state.listbox.appendChild(option);
    });

    if (items.length && state.input.dataset.suggestAll) {
      // Last of the options, reached by the arrows as the others, drawn as
      // the quick links of the site (link/quicklink.html.twig): a green
      // chevron, then the label in small capitals.
      const all = document.createElement('li');
      all.id = state.listbox.id + '-all';
      all.setAttribute('role', 'option');
      all.setAttribute('aria-selected', 'false');
      all.className = 'tw:group tw:flex tw:items-center tw:gap-1 tw:mt-2 tw:pl-4 tw:pr-3 tw:pt-3 tw:pb-2 tw:border-t tw:border-gray-200 tw:cursor-pointer tw:aria-selected:bg-green-100 tw:hover:bg-green-50';
      const chevron = document.createElement('span');
      chevron.className = 'tw:flex-none tw:size-6 tw:text-green-500';
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      svg.setAttribute('class', 'tw:w-full tw:h-auto tw:fill-current');
      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      use.setAttribute('href', '/themes/custom/vdl/src/icons/remixicon.symbol.v4.6.svg#ri-arrow-right-s-line');
      svg.appendChild(use);
      chevron.appendChild(svg);
      all.appendChild(chevron);
      const label = document.createElement('span');
      label.className = 'tw:font-bold! tw:text-xs! tw:uppercase tw:text-blue-500! tw:group-hover:text-green-500!';
      label.textContent = state.input.dataset.suggestAll;
      all.appendChild(label);
      all.addEventListener('click', () => this.submit(state));
      state.listbox.appendChild(all);
      state.items = items.concat([{ all: true }]);
    }

    // The field is narrower than its wrapper, which the list is anchored to.
    state.listbox.style.width = state.input.offsetWidth + 'px';
    state.listbox.hidden = items.length === 0;
    state.input.setAttribute('aria-expanded', items.length ? 'true' : 'false');
  }

  keydown(state, e) {
    const open = !state.listbox.hidden;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!state.items.length) {
        return;
      }
      e.preventDefault();
      if (!open) {
        state.listbox.hidden = false;
        state.input.setAttribute('aria-expanded', 'true');
      }
      const step = e.key === 'ArrowDown' ? 1 : -1;
      this.select(state, (state.active + step + state.items.length) % state.items.length);
    }
    else if (e.key === 'Enter' && open && state.active > -1) {
      e.preventDefault();
      if (state.items[state.active].all) {
        this.submit(state);
      }
      else {
        this.open(state.items[state.active]);
      }
    }
    else if (e.key === 'Escape' && open) {
      // Closes the list only: a second Escape clears the field, as the browser does.
      e.preventDefault();
      this.close(state);
    }
  }

  select(state, index) {
    Array.from(state.listbox.children).forEach((option, i) => {
      option.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
    state.active = index;
    const option = state.listbox.children[index];
    state.input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  deselect(state) {
    state.active = -1;
    state.input.removeAttribute('aria-activedescendant');
    Array.from(state.listbox.children).forEach((option) => option.setAttribute('aria-selected', 'false'));
  }

  close(state) {
    state.listbox.hidden = true;
    state.input.setAttribute('aria-expanded', 'false');
    this.deselect(state);
  }

  open(item) {
    window.location.href = item.url;
  }

  // As Enter in the field with no suggestion selected, through the button of
  // the form, the one Drupal takes as the trigger.
  submit(state) {
    this.close(state);
    const form = state.input.form;
    form.requestSubmit(form.querySelector('[type=submit]') || undefined);
  }
}
